import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  useActivityTypes,
  useActivityDurations,
  useActivityFrequencies,
  useCreatePatientActivity,
  useUpdatePatientActivity,
  useDeletePatientActivity,
} from "@/hooks/useConsultation";
import { useToast } from "@/hooks/use-toast";
import type {
  CreatePatientActivityRequest,
  UpdatePatientActivityRequest,
  Activity,
} from "@/types/appointments";
import { Search, Plus, X, ArrowLeft, Footprints } from "lucide-react";

const activitySchema = z.object({
  activity_type: z.number().min(1, "Please select an activity type"),
  activity_duration: z.number().min(1, "Please select a duration"),
  activity_frequency: z.number().min(1, "Please select a frequency"),
  units: z.string().optional(),
});

type ActivityFormData = z.infer<typeof activitySchema>;

interface AddActivityDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  appointmentId: string;
  existingEntry?: Activity;
  patientActivities?: Activity[];
}

export const AddActivityDialog = ({
  open,
  onOpenChange,
  appointmentId,
  existingEntry,
  patientActivities = [],
}: AddActivityDialogProps) => {
  const { toast } = useToast();
  const [view, setView] = useState<"manage" | "form">("manage");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedType, setSelectedType] = useState<number | null>(null);

  const { data: activityTypes = [] } = useActivityTypes();
  const { data: activityDurations = [] } = useActivityDurations();
  const { data: activityFrequencies = [] } = useActivityFrequencies();

  const createMutation = useCreatePatientActivity();
  const updateMutation = useUpdatePatientActivity();
  const deleteMutation = useDeletePatientActivity();

  const isEditing = !!existingEntry;
  const isLoading =
    createMutation.isPending ||
    updateMutation.isPending ||
    deleteMutation.isPending;

  // Reset view when dialog opens/closes or existingEntry changes
  useEffect(() => {
    if (open) {
      if (existingEntry) {
        setView("form");
        setSelectedType(existingEntry.activity_type);
      } else {
        setView("manage");
        setSearchTerm("");
        setSelectedType(null);
      }
    }
  }, [open, existingEntry]);

  const form = useForm<ActivityFormData>({
    resolver: zodResolver(activitySchema),
    defaultValues: {
      activity_type: existingEntry?.activity_type || 0,
      activity_duration: existingEntry?.activity_duration || 0,
      activity_frequency: existingEntry?.activity_frequency || 0,
      units: existingEntry?.units || "",
    },
  });

  // Update form values when switching to form view or selecting a type
  useEffect(() => {
    if (selectedType && view === "form") {
      form.setValue("activity_type", selectedType);
    }
    if (existingEntry && view === "form") {
      form.reset({
        activity_type: existingEntry.activity_type,
        activity_duration: existingEntry.activity_duration,
        activity_frequency: existingEntry.activity_frequency,
        units: existingEntry.units || "",
      });
    } else if (!existingEntry && view === "form" && selectedType) {
      // New entry defaults
      form.reset({
        activity_type: selectedType,
        activity_duration: 0,
        activity_frequency: 0,
        units: "",
      });
    }
  }, [selectedType, view, existingEntry, form]);

  const handleAddClick = (typeId: number) => {
    setSelectedType(typeId);
    setView("form");
  };

  const handleRemoveClick = async (activity: Activity) => {
    try {
      await deleteMutation.mutateAsync({
        appointmentId,
        activityId: activity.id,
      });
      toast({
        title: "Success",
        description: "Activity removed successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to remove activity",
        variant: "destructive",
      });
    }
  };

  const onSubmit = async (data: ActivityFormData) => {
    try {
      if (isEditing && existingEntry) {
        const requestData: UpdatePatientActivityRequest = {
          activity_duration: data.activity_duration,
          activity_frequency: data.activity_frequency,
          ...(data.units && { units: data.units }),
        };

        await updateMutation.mutateAsync({
          appointmentId,
          activityId: existingEntry.id,
          data: requestData,
        });

        toast({
          title: "Success",
          description: "Activity updated successfully",
        });
        onOpenChange(false);
      } else {
        const requestData: CreatePatientActivityRequest = {
          activity_type: data.activity_type,
          activity_duration: data.activity_duration,
          activity_frequency: data.activity_frequency,
          ...(data.units && { units: data.units }),
        };

        await createMutation.mutateAsync({
          appointmentId,
          data: requestData,
        });

        toast({
          title: "Success",
          description: "Activity added successfully",
        });
        // Go back to manage view
        setView("manage");
        setSelectedType(null);
      }

      form.reset();
    } catch (error) {
      toast({
        title: "Error",
        description: isEditing
          ? "Failed to update activity"
          : "Failed to add activity",
        variant: "destructive",
      });
    }
  };

  // Filter available types
  const addedTypeIds = new Set(patientActivities.map((a) => a.activity_type));

  const availableTypes = activityTypes.filter((type) => {
    const matchesSearch = searchTerm
      ? type.name.toLowerCase().includes(searchTerm.toLowerCase())
      : true;
    const notAdded = !addedTypeIds.has(type.id);
    return matchesSearch && notAdded;
  });

  // Limit to 5 if no search term, otherwise show all matching
  const displayedTypes = searchTerm
    ? availableTypes
    : availableTypes.slice(0, 5);

  // Helper to get name
  const getTypeName = (id: number) =>
    activityTypes.find((t) => t.id === id)?.name || `Activity ${id}`;
  const getDurationName = (id: number) =>
    activityDurations.find((d) => d.id === id)?.duration || "";
  const getFrequencyName = (id: number) =>
    activityFrequencies.find((f) => f.id === id)?.frequency || "";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] h-[600px] flex flex-col p-0 gap-0 overflow-hidden border-border/60">
        <DialogHeader className="p-6 pb-0 shrink-0">
          <DialogTitle className="z-h2 text-foreground flex items-center gap-2">
            {view === "form" && !isEditing && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 -ml-2 mr-1"
                onClick={() => setView("manage")}
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
            )}
            {view === "form"
              ? isEditing
                ? "Edit Activity"
                : "Add Details"
              : "Add Activities"}
          </DialogTitle>
        </DialogHeader>
        <Separator className="mt-3" />

        {view === "manage" ? (
          <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
            {/* Already Added Section */}
            <div className="px-6 pb-4 border-b border-border/60 shrink-0">
              <h3 className="z-eyebrow text-muted-foreground mb-3">
                Already Added
              </h3>
              {patientActivities.length > 0 ? (
                <div className="grid grid-cols-2 gap-2.5 max-h-[160px] overflow-y-auto">
                  {patientActivities.map((activity) => (
                    <div
                      key={activity.id}
                      className="bg-card p-3 rounded-xl border border-border shadow-sm relative group"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="z-body-md text-foreground">
                            {getTypeName(activity.activity_type)}
                          </p>
                          <div className="mt-1 space-y-0.5 z-caption text-muted-foreground">
                            <p>{getDurationName(activity.activity_duration)}</p>
                            <p>
                              {getFrequencyName(activity.activity_frequency)}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => handleRemoveClick(activity)}
                          className="shrink-0 rounded-full p-0.5 transition-colors text-muted-foreground/60 hover:bg-destructive/10 hover:text-destructive"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="z-secondary text-muted-foreground/60 italic py-2">
                  No activities added yet
                </p>
              )}
            </div>

            {/* Add More Section */}
            <div className="flex-1 flex flex-col min-h-0 bg-background">
              <div className="p-4 border-b border-border/60 bg-background z-10">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground/60" />
                  <Input
                    placeholder="Search for more activities..."
                    className="pl-9 bg-muted/30 border-border/50 rounded-xl z-body placeholder:text-muted-foreground/50"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-6">
                <h3 className="z-label text-muted-foreground mb-3">
                  {searchTerm ? "Search Results" : "Popular Activities"}
                </h3>
                <div className="grid grid-cols-3 gap-2.5">
                  {displayedTypes.map((type) => (
                    <button
                      key={type.id}
                      onClick={() => handleAddClick(type.id)}
                      className="flex flex-col items-start p-3 rounded-xl border border-border bg-card hover:border-primary/40 hover:shadow-sm transition-all text-left relative group"
                    >
                      <div className="mb-2 p-1.5 bg-primary/10 text-primary rounded-lg">
                        <Footprints className="h-4 w-4" />
                      </div>
                      <span className="z-body-md text-foreground line-clamp-2">
                        {type.name}
                      </span>

                      <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="h-5 w-5 bg-primary rounded-full flex items-center justify-center shadow-sm text-primary-foreground">
                          <Plus className="h-3 w-3" />
                        </div>
                      </div>
                    </button>
                  ))}
                  {availableTypes.length === 0 && (
                    <div className="col-span-3 text-center py-8 text-muted-foreground z-body">
                      No matching activities found
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto p-6">
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="space-y-6"
              >
                {!isEditing && (
                  <div className="p-4 bg-primary/5 rounded-xl border border-primary/20 mb-6">
                    <h3 className="z-body-md text-foreground">
                      {selectedType
                        ? getTypeName(selectedType)
                        : "New Activity"}
                    </h3>
                  </div>
                )}

                {/* Hidden activity_type field since we selected it */}
                <div className="grid grid-cols-1 gap-6">
                  <FormField
                    control={form.control}
                    name="activity_duration"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Duration</FormLabel>
                        <Select
                          onValueChange={(value) =>
                            field.onChange(parseInt(value))
                          }
                          value={field.value ? field.value.toString() : ""}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select from dropdown" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {activityDurations.map((duration) => (
                              <SelectItem
                                key={duration.id}
                                value={duration.id.toString()}
                              >
                                {duration.duration}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="activity_frequency"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Frequency</FormLabel>
                        <Select
                          onValueChange={(value) =>
                            field.onChange(parseInt(value))
                          }
                          value={field.value ? field.value.toString() : ""}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select from dropdown" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {activityFrequencies.map((freq) => (
                              <SelectItem
                                key={freq.id}
                                value={freq.id.toString()}
                              >
                                {freq.frequency}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="units"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Units (optional)</FormLabel>
                      <FormControl>
                        <Input placeholder="e.g., steps, calories" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="flex justify-end pt-4">
                  <Button
                    type="submit"
                    disabled={isLoading}
                    className="w-full sm:w-auto"
                  >
                    {isLoading
                      ? "Saving..."
                      : isEditing
                        ? "Update Activity"
                        : "Add Activity"}
                  </Button>
                </div>
              </form>
            </Form>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
